// Modelo del Salón: estados de mesa, lienzo del plano y agrupación de mesas unidas.

export type Estado = 'libre' | 'ocupada' | 'por_cobrar' | 'reservada' | 'limpieza' | 'unida';

export interface Mesa {
  id: string;
  numero: number;
  capacidad: number;
  estado: Estado;
  zona: string | null;
  ocupada_desde: string | null;
  personas_actual: number;
  mesa_unida_con: string | null;
  unida_con_numero: number | null;
  mesero: string | null;
  total: number;
  listos: number;
  pos_x: number | null;
  pos_y: number | null;
  ancho: number | null;
  alto: number | null;
  forma: 'cuadrada' | 'redonda';
}

export interface Area {
  id?: string;
  zona: string | null;
  nombre: string;
  pos_x: number; pos_y: number; ancho: number; alto: number;
}

// Lienzo lógico por zona (igual que el CHECK de la migración 037).
export const LIENZO = { w: 1000, h: 620 } as const;

export const ESTADOS: Record<Estado, { label: string; bg: string; bd: string; fg: string; borde: 'solid' | 'dashed' }> = {
  libre:      { label: 'Libre',      bg: 'var(--libre-bg)',  bd: 'var(--libre-bd)',  fg: 'var(--libre-fg)',  borde: 'solid' },
  ocupada:    { label: 'Ocupada',    bg: 'var(--strong)',    bd: 'var(--strong)',    fg: 'var(--on-strong)', borde: 'solid' },
  por_cobrar: { label: 'Por cobrar', bg: 'var(--cobrar-bg)', bd: 'var(--cobrar-bd)', fg: 'var(--cobrar-fg)', borde: 'solid' },
  reservada:  { label: 'Reservada',  bg: 'var(--res-bg)',    bd: 'var(--res-bd)',    fg: 'var(--res-fg)',    borde: 'solid' },
  limpieza:   { label: 'Limpieza',   bg: 'var(--limp-bg)',   bd: 'var(--limp-bd)',   fg: 'var(--ink-2)',     borde: 'dashed' },
  unida:      { label: 'Unida',      bg: 'var(--strong)',    bd: 'var(--strong)',    fg: 'var(--on-strong)', borde: 'solid' },
};

export const ORDEN_LEYENDA: Estado[] = ['libre', 'ocupada', 'por_cobrar', 'reservada', 'limpieza', 'unida'];

export const SIN_ZONA = 'Salón';
export const zonaDe = (m: { zona: string | null }) => m.zona?.trim() || SIN_ZONA;

export const minutosDesde = (iso: string | null) =>
  iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)) : 0;

// "45 min", "2 h 10 min", "3 días": una mesa olvidada abierta no desborda la tarjeta.
export function duracion(min: number) {
  if (min < 60) return `${min} min`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}`;
  const d = Math.floor(min / (24 * 60));
  return `${d} ${d === 1 ? 'día' : 'días'}`;
}

// Mesa que nunca se ha acomodado: se coloca en rejilla dentro de su zona,
// en el orden de número, hasta que alguien la mueva con "Acomodar plano".
const COLS = 6, CELDA_W = 160, CELDA_H = 150, MARGEN = 30;
export function conPosicion(mesas: Mesa[]): Mesa[] {
  const indice: Record<string, number> = {};
  return mesas.map(m => {
    if (m.pos_x != null && m.pos_y != null && m.ancho && m.alto) return m;
    const z = zonaDe(m);
    const i = indice[z] = (indice[z] ?? -1) + 1;
    const ancho = m.capacidad >= 6 ? 140 : 120;
    return {
      ...m,
      pos_x: Math.min(LIENZO.w - ancho, MARGEN + (i % COLS) * CELDA_W),
      pos_y: Math.min(LIENZO.h - 110, MARGEN + Math.floor(i / COLS) * CELDA_H),
      ancho, alto: 110,
    };
  });
}

// Una mesa unida y su pareja se muestran como UNA sola: la de número menor
// es la principal y carga la suma de ambas.
export interface Grupo {
  principal: Mesa;
  miembros: Mesa[];
  estado: Estado;
  total: number;
  listos: number;
  etiqueta: string;
}

export function agrupar(mesas: Mesa[]): Grupo[] {
  const porId = new Map(mesas.map(m => [m.id, m]));
  const vistos = new Set<string>();
  const grupos: Grupo[] = [];
  for (const m of mesas) {
    if (vistos.has(m.id)) continue;
    const pareja = m.mesa_unida_con ? porId.get(m.mesa_unida_con) : undefined;
    const miembros = pareja ? [m, pareja].sort((a, b) => a.numero - b.numero) : [m];
    miembros.forEach(x => vistos.add(x.id));
    grupos.push({
      principal: miembros[0],
      miembros,
      // Si alguna ya pidió la cuenta, eso manda: Caja tiene que verla.
      estado: miembros.some(x => x.estado === 'por_cobrar') ? 'por_cobrar' : pareja ? 'unida' : m.estado,
      total: miembros.reduce((s, x) => s + (x.total || 0), 0),
      listos: miembros.reduce((s, x) => s + (x.listos || 0), 0),
      etiqueta: miembros.map(x => x.numero).join(' + '),
    });
  }
  return grupos;
}

// Caja que envuelve a todos los miembros (en el plano se dibujan como una).
export function cajaDe(g: Grupo) {
  const xs = g.miembros;
  const x = Math.min(...xs.map(m => m.pos_x!)), y = Math.min(...xs.map(m => m.pos_y!));
  return {
    x, y,
    w: Math.max(...xs.map(m => m.pos_x! + m.ancho!)) - x,
    h: Math.max(...xs.map(m => m.pos_y! + m.alto!)) - y,
  };
}

export const TIEMPOS: Record<number, string> = { 1: '1er tiempo', 2: '2º tiempo', 3: 'Postre' };

export const ESTADO_ITEM: Record<string, { label: string; bg: string; fg: string }> = {
  listo:          { label: 'Listo',          bg: 'var(--libre-bg)',  fg: 'var(--libre-fg)' },
  en_preparacion: { label: 'En preparación', bg: 'var(--cobrar-bg)', fg: 'var(--cobrar-fg)' },
  pendiente:      { label: 'Pendiente',      bg: 'var(--well)',      fg: 'var(--ink-2)' },
  entregado:      { label: 'Servido',        bg: 'var(--bg)',        fg: 'var(--muted)' },
};
